import CancelOutlined from '@mui/icons-material/CancelOutlined';
import SvgIcon from '@mui/icons-material/CancelOutlined';
import Check from '@mui/icons-material/Check';
import ExpandMoreOutlined from '@mui/icons-material/ExpandMoreOutlined';
import FiberManualRecord from '@mui/icons-material/FiberManualRecord';
import Pause from '@mui/icons-material/Pause';
import PlayArrow from '@mui/icons-material/PlayArrow';
import SmartToyOutlined from '@mui/icons-material/SmartToyOutlined';
import Box from '@mui/material/Box';
import { Theme, useTheme } from '@mui/material/styles';
import { SxProps } from '@mui/system';
import { Subscription } from '@rails/actioncable';
import { AiReviewStatus, JobEventName } from '@treadinc/horizon-api-spec';
import dayjs, { Dayjs } from 'dayjs';
import { t } from 'i18next';
import { snakeCase } from 'lodash-es';
import React, { createElement, useEffect, useMemo, useState } from 'react';
import { Trans } from 'react-i18next';

import { Job, JobTripEvent } from '~hooks/useJob/models';
import { useJob } from '~hooks/useJob/useJob';
import { AiReviewBadge } from '~pages/Approvals/Files/components/LoadsTable/components/AiReviewIndicator';
import { useStores } from '~store/RootStore';
import { getEffectiveUserCompanyId } from '~utils/user/user-utils';

import {
  EVENT_TIMELINE_JOB_EVENT_TIME_FORMAT,
  EventTimeline,
  EventTimelineConnector,
  EventTimelineContent,
  EventTimelineContentContainer,
  EventTimelineDot,
  EventTimelineDotProps,
  EventTimelineIcon,
  EventTimelineItem,
  EventTimelinePrimaryText,
  EventTimelineSecondaryText,
  EventTimelineSeparator,
} from '../EventTimeline/EventTimeline';

const stateChangeJobEvents: JobEventName[] = [
  JobEventName.JOB_ACCEPTED,
  JobEventName.JOB_ASSIGNED,
  JobEventName.JOB_ATTRIBUTE_CHANGED,
  JobEventName.JOB_CANCELED,
  JobEventName.JOB_COMPLETED,
  JobEventName.JOB_CREATED,
  JobEventName.JOB_DECLINED,
  JobEventName.JOB_IN_REVIEW,
  JobEventName.JOB_PAUSED,
  JobEventName.JOB_REQUESTED,
  JobEventName.JOB_RESUMED,
  JobEventName.JOB_SENT,
  JobEventName.JOB_UNASSIGNED,
  JobEventName.TICKET_CHASE_INITIATED,
  JobEventName.TICKETS_UPLOADED_BY_AGENT,
];

enum JobTimelineEventType {
  LOAD_CHANGE = 'load_change',
  NOTIFICATION_GROUP = 'notification_group',
  STATE_CHANGE = 'state_change',
}

// Time window in minutes for grouping consecutive notification events
const NOTIFICATION_GROUPING_WINDOW_MINUTES = 5;

type JobTimelineEntry = { type: JobTimelineEventType; timeline: JobTripEvent };

interface JobTimelineComponentProps {
  isExpanded?: boolean;
  isGrouped?: boolean;
  isLast?: boolean;
  loadNumber?: number;
  onExpand?: () => void;
  timeline: JobTripEvent;
}

const componentsByJobTimelineEventType: Record<
  JobTimelineEventType,
  React.JSXElementConstructor<JobTimelineComponentProps>
> = {
  [JobTimelineEventType.LOAD_CHANGE]: LoadChangeTimelineItem,
  [JobTimelineEventType.NOTIFICATION_GROUP]: NotificationGroupTimelineItem,
  [JobTimelineEventType.STATE_CHANGE]: StateChangeTimelineItem,
};

const getJobEventIconsByJobEventName = (
  theme: Theme,
): Partial<Record<JobEventName, { icon: typeof SvgIcon; color: string }>> => ({
  [JobEventName.JOB_CANCELED]: {
    icon: CancelOutlined,
    color: theme.brandV2.colors.treadRed,
  },
  [JobEventName.JOB_DECLINED]: {
    icon: CancelOutlined,
    color: theme.brandV2.colors.treadRed,
  },
  [JobEventName.LOAD_CANCELED]: {
    icon: CancelOutlined,
    color: theme.brandV2.colors.treadRed,
  },
});

const getTimelineDotIconByJobEventName = (
  theme: Theme,
): Partial<
  Record<JobEventName, { icon: typeof SvgIcon; color: string; size: number }>
> => ({
  [JobEventName.JOB_COMPLETED]: {
    icon: Check,
    color: theme.brandV2.colors.treadGreen,
    size: 12,
  },
  [JobEventName.JOB_CREATED]: {
    icon: FiberManualRecord,
    color: theme.brandV2.colors.treadGreen,
    size: 8,
  },
  [JobEventName.JOB_PAUSED]: {
    icon: Pause,
    color: theme.brandV2.colors.treadYellowDark,
    size: 12,
  },
  [JobEventName.JOB_RESUMED]: {
    icon: PlayArrow,
    color: theme.brandV2.colors.treadGreen,
    size: 12,
  },
  [JobEventName.TICKET_CHASE_INITIATED]: {
    icon: SmartToyOutlined,
    color: theme.brandV2.colors.treadBlue,
    size: 12,
  },
  [JobEventName.TICKETS_UPLOADED_BY_AGENT]: {
    icon: SmartToyOutlined,
    color: theme.brandV2.colors.treadBlue,
    size: 12,
  },
});

// Historical timeline payloads may carry values outside the enum; treat them as flagged.
const getAiReviewStatus = (timeline: JobTripEvent) =>
  Object.values(AiReviewStatus).find((status) => status === timeline.payload?.value) ??
  AiReviewStatus.FLAGGED;

export const getEventLabel = (timeline: JobTripEvent) => {
  let jobEventsTranslationKey = snakeCase(timeline.eventName);
  const label = timeline.payload?.label;

  if (timeline.eventName === JobEventName.LOAD_AI_REVIEWED) {
    return <>{t(`job_events.load_ai_reviewed_${getAiReviewStatus(timeline)}`)}</>;
  }

  if (timeline.eventName === JobEventName.JOB_ASSIGNED) {
    // old events won't have a label, so we need to use "assigned" instead of "assigned to"
    jobEventsTranslationKey = label ? `job_assigned` : `assigned`;
  }

  // for these events, we want to specify if the resource was removed
  if (
    timeline.eventName === JobEventName.JOB_EQUIPMENT_CHANGED ||
    timeline.eventName === JobEventName.JOB_MATERIAL_CHANGED
  ) {
    // add the "removed" suffix to the translation key
    if (!label) {
      jobEventsTranslationKey += `_removed`;
    }
  }

  if (timeline.eventName === JobEventName.TICKETS_UPLOADED_BY_AGENT) {
    const { value } = timeline.payload || {};

    if (value) {
      const count = Number(value);
      return (
        <>
          <Trans
            i18nKey="job_events.tickets_uploaded_by_agent"
            values={{
              count,
              plural: count !== 1 ? 's' : '',
            }}
            components={[<strong key="0" />]}
          />
        </>
      );
    }
  }

  if (timeline.eventName === JobEventName.JOB_ATTRIBUTE_CHANGED) {
    const { from_value, to_value } = timeline.payload || {};

    if (from_value && to_value) {
      return (
        <>
          <strong>{label}</strong>{' '}
          <Trans
            i18nKey="job_events.changed_from_to"
            values={{ from: from_value, to: to_value }}
            components={[<strong key="0" />, <strong key="1" />]}
          />
        </>
      );
    }
    if (!from_value && to_value) {
      return (
        <>
          <strong>{label}</strong>{' '}
          <Trans
            i18nKey="job_events.changed_to"
            values={{ to: to_value }}
            components={[<strong key="0" />]}
          />
        </>
      );
    }
    if (from_value && !to_value) {
      return (
        <>
          <strong>{label}</strong>{' '}
          <Trans
            i18nKey="job_events.removed_was"
            values={{ from: from_value }}
            components={[<strong key="0" />]}
          />
        </>
      );
    }
    return (
      <>
        <strong>{label}</strong> {t('job_events.changed')}
      </>
    );
  }

  return (
    <>
      {t(`job_events.${jobEventsTranslationKey}`)}
      {label && <strong>{label}</strong>}
    </>
  );
};

const isNotificationSentEvent = (event: JobTripEvent): boolean => {
  return snakeCase(event.eventName) === 'notification_sent';
};

const isWithinNotificationGroupingWindow = (
  eventTime: Dayjs,
  groupTime: Dayjs,
): boolean => {
  const differenceInMinutes = Math.abs(eventTime.diff(groupTime, 'minute'));
  return differenceInMinutes <= NOTIFICATION_GROUPING_WINDOW_MINUTES;
};

export const groupEvents = (timeline: JobTripEvent[]) => {
  const loadGroupIndexByLoadId = new Map<string, number>();
  const notificationPointer = { index: -1, latestTime: null as Dayjs | null };

  const groupedTimeline = timeline.reduce(
    (acc, event) => {
      const isStateChangeEvent = stateChangeJobEvents.includes(event.eventName);
      const isNotificationEvent = isNotificationSentEvent(event);

      // Handle notification events - group within time window
      if (isNotificationEvent) {
        const lastNotificationGroup =
          notificationPointer.index >= 0 ? acc[notificationPointer.index] : null;
        const canGroupWithPrevious =
          lastNotificationGroup &&
          lastNotificationGroup.type === JobTimelineEventType.NOTIFICATION_GROUP &&
          notificationPointer.latestTime &&
          isWithinNotificationGroupingWindow(
            event.createdAt,
            notificationPointer.latestTime,
          );

        if (canGroupWithPrevious) {
          (lastNotificationGroup.entries as JobTimelineEntry[]).push({
            type: JobTimelineEventType.NOTIFICATION_GROUP,
            timeline: event,
          });
          // Update the latest time in the group to the oldest event (since timeline is newest first)
          notificationPointer.latestTime = event.createdAt;
        } else {
          notificationPointer.index = acc.length;
          notificationPointer.latestTime = event.createdAt;

          acc.push({
            createdAt: event.createdAt,
            type: JobTimelineEventType.NOTIFICATION_GROUP,
            entries: [{ type: JobTimelineEventType.NOTIFICATION_GROUP, timeline: event }],
          });
        }
        return acc;
      }

      // Reset notification pointer when a non-notification event is encountered
      notificationPointer.index = -1;
      notificationPointer.latestTime = null;

      if (isStateChangeEvent || event.loadId === null) {
        acc.push({
          createdAt: event.createdAt,
          type: JobTimelineEventType.STATE_CHANGE,
          entries: { type: JobTimelineEventType.STATE_CHANGE, timeline: event },
        });
        return acc;
      }

      const loadId = String(event.loadId);
      const existingGroupIndex = loadGroupIndexByLoadId.get(loadId);

      if (existingGroupIndex !== undefined) {
        (acc[existingGroupIndex].entries as JobTimelineEntry[]).push({
          type: JobTimelineEventType.LOAD_CHANGE,
          timeline: event,
        });
      } else {
        loadGroupIndexByLoadId.set(loadId, acc.length);

        acc.push({
          createdAt: event.createdAt,
          type: JobTimelineEventType.LOAD_CHANGE,
          entries: [{ type: JobTimelineEventType.LOAD_CHANGE, timeline: event }],
        });
      }

      return acc;
    },
    [] as Array<{
      createdAt: Dayjs;
      type: JobTimelineEventType;
      entries: JobTimelineEntry | JobTimelineEntry[];
    }>,
  );

  return groupedTimeline;
};

interface JobTimelineProps {
  job: Job;
  sx?: SxProps;
}

export default function JobTimeline({ job, sx }: JobTimelineProps) {
  const { getAllJobEvents, subscribeToJobEventUpdates } = useJob();
  const [timeline, setTimeline] = useState<JobTripEvent[]>([]);
  const [expandedIndexes, setExpandedIndexes] = useState<number[]>([]);
  const { userStore } = useStores();
  const companyId = getEffectiveUserCompanyId(userStore);

  const events = useMemo(() => groupEvents(timeline), [timeline]);
  let loadNumber =
    events.filter(
      (event) =>
        event.type === JobTimelineEventType.LOAD_CHANGE && Array.isArray(event.entries),
    ).length + 1;

  useEffect(() => {
    getAllJobEvents(job.id, companyId).then(setTimeline);
  }, [job.id, job.status]);

  useEffect(() => {
    let subscription: Subscription | undefined;

    if (companyId && job.id) {
      subscription = subscribeToJobEventUpdates({
        jobId: job.id,
        onMessageCallBack: (jobEvent) => {
          setTimeline((currentTimeline) => {
            // if the event already exists in the timeline, update it
            const existingIndex = currentTimeline.findIndex(
              (event) => event.id === jobEvent.id,
            );

            let updatedTimeline: JobTripEvent[];

            // otherwise, append it as the most recent event
            if (existingIndex !== -1) {
              updatedTimeline = [...currentTimeline];
              updatedTimeline[existingIndex] = jobEvent;
            } else {
              updatedTimeline = [jobEvent, ...currentTimeline];
            }

            return updatedTimeline;
          });
        },
      });
    }

    return () => {
      subscription?.unsubscribe();
    };
  }, [companyId, job.id, subscribeToJobEventUpdates]);

  const handleExpandToggle = (index: number) => {
    setExpandedIndexes((currentExpandedIndexes) => {
      const newCurrentExpandedIndexes = [...currentExpandedIndexes];
      const indexPositionInList = newCurrentExpandedIndexes.findIndex((i) => i === index);

      if (indexPositionInList > -1) {
        newCurrentExpandedIndexes.splice(indexPositionInList, 1);
      } else {
        newCurrentExpandedIndexes.push(index);
      }

      return newCurrentExpandedIndexes;
    });
  };

  return (
    <Box sx={{ width: '100%', ...sx }}>
      <EventTimeline>
        {events.map((item, index) => {
          const isGroupedEvent = Array.isArray(item.entries);
          const isNotificationGroup =
            item.type === JobTimelineEventType.NOTIFICATION_GROUP;
          const isLoadGroup =
            item.type === JobTimelineEventType.LOAD_CHANGE && isGroupedEvent;

          // Handle notification groups
          if (isNotificationGroup && isGroupedEvent) {
            const entries = item.entries as JobTimelineEntry[];
            const isExpanded = expandedIndexes.includes(index);
            const isExpansible = entries.length > 1;

            return entries.map((entry, entryIndex) => {
              const isFirst = entryIndex === 0;

              if (isFirst || isExpanded) {
                return createElement(componentsByJobTimelineEventType[item.type], {
                  key: `${index}-${entryIndex}`,
                  isExpanded,
                  isGrouped: entryIndex !== 0,
                  isLast: index === events.length - 1,
                  loadNumber: entries.length,
                  timeline: entry.timeline,
                  onExpand:
                    isExpansible && isFirst ? () => handleExpandToggle(index) : undefined,
                });
              }

              return null;
            });
          }

          // Handle load groups
          if (isLoadGroup) {
            const entries = item.entries as JobTimelineEntry[];
            loadNumber = loadNumber - 1;

            return entries.map((entry, entryIndex) => {
              const isFirst = entryIndex === 0;
              const isExpanded = expandedIndexes.includes(index);
              const isExpansible = Boolean(isFirst && entries.length > 1);

              if (isFirst || isExpanded) {
                return createElement(componentsByJobTimelineEventType[item.type], {
                  key: `${index}-${entryIndex}`,
                  isExpanded: isExpanded,
                  isGrouped: entryIndex !== 0,
                  isLast: index === events.length - 1,
                  loadNumber,
                  timeline: entry.timeline,
                  onExpand: isExpansible ? () => handleExpandToggle(index) : undefined,
                });
              }

              return null;
            });
          }

          const entry = item.entries as JobTimelineEntry;

          return createElement(componentsByJobTimelineEventType[item.type], {
            key: index,
            isLast: index === events.length - 1,
            timeline: entry.timeline,
          });
        })}
      </EventTimeline>
    </Box>
  );
}

function StateChangeTimelineItem({ isLast, timeline }: JobTimelineComponentProps) {
  const theme = useTheme();
  const state = timeline.eventName;
  const icon = getJobEventIconsByJobEventName(theme)[state];

  const dotIcon = getTimelineDotIconByJobEventName(theme)[state];
  const dotProps = useMemo(() => {
    const props: EventTimelineDotProps = {
      shape: 'dot',
      innerColor: theme.brandV2.colors.treadGreen,
    };

    if (
      [
        JobEventName.TICKET_CHASE_INITIATED,
        JobEventName.TICKETS_UPLOADED_BY_AGENT,
      ].includes(state)
    ) {
      props.innerColor = theme.brandV2.colors.treadBlue;

      return props;
    }

    if (
      [
        JobEventName.JOB_CANCELED,
        JobEventName.JOB_DECLINED,
        JobEventName.LOAD_CANCELED,
      ].includes(state)
    ) {
      props.innerColor = theme.brandV2.colors.treadRed;

      return props;
    }

    if (state === JobEventName.JOB_UNASSIGNED) {
      props.shape = 'square';
      props.innerColor = theme.brandV2.colors.treadRed;

      return props;
    }

    return props;
  }, [state, theme]);

  return (
    <EventTimelineItem>
      <EventTimelineSeparator>
        <EventTimelineDot
          innerColor={dotIcon?.color ?? dotProps.innerColor}
          shape={dotIcon ? 'icon' : dotProps.shape}
        >
          {dotIcon &&
            createElement(EventTimelineIcon, {
              icon: dotIcon.icon,
              sx: {
                color: dotIcon.color,
                width: `${dotIcon.size}px`,
                height: `${dotIcon.size}px`,
              },
            })}
        </EventTimelineDot>

        {!isLast && <EventTimelineConnector />}
      </EventTimelineSeparator>

      <EventTimelineContentContainer hasDotIconAtLeft={Boolean(dotIcon)}>
        <EventTimelineContent
          date={timeline.createdAt}
          sx={{ ...(dotIcon ? { pt: '4px' } : {}) }}
        >
          <EventTimelinePrimaryText sx={{ color: icon?.color }}>
            {icon &&
              createElement(icon.icon, {
                sx: { color: icon.color, fontSize: '16px', mt: '-2px', mr: 0.25 },
              })}

            {getEventLabel(timeline)}
          </EventTimelinePrimaryText>

          <EventTimelineSecondaryText>
            {timeline.createdByName}
          </EventTimelineSecondaryText>
        </EventTimelineContent>
      </EventTimelineContentContainer>
    </EventTimelineItem>
  );
}

function LoadChangeTimelineItem({
  isExpanded,
  isGrouped,
  isLast,
  loadNumber,
  onExpand,
  timeline,
}: JobTimelineComponentProps) {
  const theme = useTheme();
  const state = timeline.eventName;
  const isLoadCompleted = state === JobEventName.LOAD_COMPLETED;
  const isLoadAiReviewed = state === JobEventName.LOAD_AI_REVIEWED;
  const icon = getJobEventIconsByJobEventName(theme)[state];
  const dotProps = useMemo(() => {
    const props: EventTimelineDotProps = {
      shape: 'icon',
      innerColor: theme.brandV2.colors.treadGray7,
    };

    if (isGrouped && isLast) {
      props.shape = 'dot';
      props.innerColor = theme.brandV2.colors.treadGray7;
    } else if (isGrouped) {
      props.shape = 'empty';
    }

    return props;
  }, [isGrouped, isLast, theme]);

  return (
    <EventTimelineItem>
      <EventTimelineSeparator>
        <EventTimelineDot innerColor={dotProps.innerColor} shape={dotProps.shape}>
          {!isGrouped && (
            <EventTimelineIcon
              icon={ExpandMoreOutlined}
              onClick={onExpand}
              sx={{
                cursor: 'pointer',
                transform: isExpanded ? 'rotate(0deg)' : 'rotate(-90deg)',
                transition: 'transform .2s linear',
              }}
            />
          )}
        </EventTimelineDot>

        {!isLast && <EventTimelineConnector />}
      </EventTimelineSeparator>

      <EventTimelineContentContainer
        hasDotIconAtLeft={!isGrouped}
        sx={{ ...(isGrouped ? { pl: '18px' } : {}) }}
      >
        <EventTimelineContent
          {...(isGrouped
            ? { date: timeline.createdAt }
            : { label: `${t('status.load')} ${loadNumber}`, sx: { pt: '4px' } })}
        >
          {isGrouped ? (
            <>
              <EventTimelinePrimaryText sx={{ color: icon?.color }}>
                {icon &&
                  createElement(icon.icon, {
                    sx: { color: icon.color, fontSize: '16px', mt: '-2px', mr: 0.25 },
                  })}

                {getEventLabel(timeline)}
              </EventTimelinePrimaryText>
              <EventTimelineSecondaryText>
                {timeline.createdByName}
              </EventTimelineSecondaryText>
            </>
          ) : (
            <EventTimelinePrimaryText sx={{ color: icon?.color }}>
              {isLoadAiReviewed && (
                <Box component="span" sx={{ mr: 0.5 }}>
                  <AiReviewBadge status={getAiReviewStatus(timeline)} size="sm" />
                </Box>
              )}

              {icon &&
                createElement(icon.icon, {
                  sx: { color: icon.color, fontSize: '16px', mt: '-2px', mr: 0.25 },
                })}

              {isLoadCompleted ? t('status.delivered') : getEventLabel(timeline)}
              <EventTimelineSecondaryText component="span">{` • ${dayjs.tz(timeline.createdAt).format(EVENT_TIMELINE_JOB_EVENT_TIME_FORMAT)}`}</EventTimelineSecondaryText>
            </EventTimelinePrimaryText>
          )}
        </EventTimelineContent>
      </EventTimelineContentContainer>
    </EventTimelineItem>
  );
}

function NotificationGroupTimelineItem({
  isExpanded,
  isGrouped,
  isLast,
  loadNumber: notificationCount,
  onExpand,
  timeline,
}: JobTimelineComponentProps) {
  const theme = useTheme();
  const hasMultipleNotifications = (notificationCount ?? 1) > 1;
  const isGroupHeader = !isGrouped && hasMultipleNotifications;

  const dotProps = useMemo(() => {
    const props: EventTimelineDotProps = {
      shape: 'dot',
      innerColor: theme.brandV2.colors.treadGreen,
    };

    // Group header gets expand icon
    if (isGroupHeader) {
      props.shape = 'icon';
      props.innerColor = theme.brandV2.colors.treadGray7;
    } else if (isGrouped && isLast) {
      props.shape = 'dot';
      props.innerColor = theme.brandV2.colors.treadGray7;
    } else if (isGrouped) {
      props.shape = 'empty';
    }

    return props;
  }, [isGrouped, isLast, isGroupHeader, theme]);

  return (
    <EventTimelineItem>
      <EventTimelineSeparator>
        <EventTimelineDot innerColor={dotProps.innerColor} shape={dotProps.shape}>
          {isGroupHeader && (
            <EventTimelineIcon
              icon={ExpandMoreOutlined}
              onClick={onExpand}
              sx={{
                cursor: 'pointer',
                transform: isExpanded ? 'rotate(0deg)' : 'rotate(-90deg)',
                transition: 'transform .2s linear',
              }}
            />
          )}
        </EventTimelineDot>

        {!isLast && <EventTimelineConnector />}
      </EventTimelineSeparator>

      <EventTimelineContentContainer
        hasDotIconAtLeft={isGroupHeader}
        sx={{
          ...(isGrouped ? { pl: '18px' } : {}),
          ...(isGroupHeader ? { pt: '4px' } : {}),
        }}
      >
        <EventTimelineContent date={timeline.createdAt}>
          <EventTimelinePrimaryText>{getEventLabel(timeline)}</EventTimelinePrimaryText>
          <EventTimelineSecondaryText>
            {isGroupHeader
              ? t('job_events.notifications_sent_grouped_subtitle', {
                  createdBy: timeline.createdByName,
                  count: notificationCount,
                })
              : timeline.createdByName}
          </EventTimelineSecondaryText>
        </EventTimelineContent>
      </EventTimelineContentContainer>
    </EventTimelineItem>
  );
}
